import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const Section = ({ n, title, children }) => (
  <section className="mb-10" data-testid={`refunds-section-${n}`}>
    <h2 className="text-sm uppercase tracking-[0.2em] text-zinc-500 mb-3">
      {String(n).padStart(2, "0")} — {title}
    </h2>
    <div className="text-zinc-300 text-sm leading-relaxed space-y-3">{children}</div>
  </section>
);

const Refunds = () => {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-zinc-100" data-testid="refunds-page">
      <div className="max-w-3xl mx-auto px-6 py-16">
        <Link
          to="/"
          data-testid="refunds-back-link"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-500 hover:text-white transition-colors mb-12"
        >
          <ArrowLeft className="w-3 h-3" /> Back
        </Link>

        <header className="mb-14">
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Legal</div>
          <h1 className="text-4xl sm:text-5xl font-medium tracking-tight">Refund Policy</h1>
          <div className="mt-4 text-xs text-zinc-500 font-mono">Last updated: February 14, 2026</div>
        </header>

        <Section n={1} title="Scope">
          <p>
            This Refund Policy applies only to Quill SaaS subscriptions (Pro, Team, Studio) paid
            <span className="text-white"> on-chain in ETH or BTC</span> to Quill&apos;s platform
            wallet. It does <span className="text-white">not</span> apply to crypto payments
            sent directly from clients to creator wallets — those settle outside Quill&apos;s
            systems and Quill is non-custodial and never holds client funds.
          </p>
        </Section>

        <Section n={2} title="14-day money-back guarantee">
          <p>
            If you upgrade to Pro, Team, or Studio and decide it isn&apos;t for you, email{" "}
            <a
              href="mailto:billing@quillpayed.com"
              data-testid="refunds-contact-email"
              className="text-white underline underline-offset-4 decoration-zinc-700 hover:decoration-white"
            >
              billing@quillpayed.com
            </a>{" "}
            within <span className="text-white">14 days</span> of your initial on-chain payment
            and we will refund the equivalent USD value of the subscription fee, paid back to the
            wallet you originally sent from. The refund applies once per customer.
          </p>
        </Section>

        <Section n={3} title="Renewals">
          <p>
            Subscriptions <span className="text-white">do not auto-renew</span> — they are paid
            on-chain per period. Before each period ends we send a renewal notice; you can either
            send the next ETH/BTC payment to keep your plan active or let it lapse (you keep
            access through the period you already paid for). Completed on-chain renewal payments
            are non-refundable except under the 14-day guarantee in §2 (measured from your
            initial purchase, not each renewal).
          </p>
        </Section>

        <Section n={4} title="Annual plans">
          <p>
            Annual plans are eligible for a full refund under the 14-day guarantee. After 14 days,
            annual fees are non-refundable; however, you can downgrade or cancel at any time and
            you will retain access through the paid-for period.
          </p>
        </Section>

        <Section n={5} title="Taxes & fees">
          <p>
            Where a refund is issued, it is calculated on the USD-equivalent of the on-chain
            payment at the time of purchase and returned in the same asset (ETH or BTC) to the
            wallet the payment originated from. Network gas fees paid to miners/validators for
            the original transaction or the refund transaction are not recoverable. Exchange
            rate movement between purchase and refund is out of Quill&apos;s control.
          </p>
        </Section>

        <Section n={6} title="Exceptions">
          <p>We do not issue refunds for:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Accounts terminated for violation of our <Link to="/terms" className="text-white underline underline-offset-4 decoration-zinc-700 hover:decoration-white">Terms of Service</Link>.</li>
            <li>Loss of crypto funds, mistyped wallet addresses, failed on-chain transactions, or any other event involving funds outside Quill&apos;s systems.</li>
            <li>Downtime or outages of third-party services (block explorers, email delivery, authentication).</li>
            <li>Renewals beyond the 14-day window described above.</li>
          </ul>
        </Section>

        <Section n={7} title="Chargebacks">
          <p>
            Quill subscription payments are on-chain and cannot be charged back. Please contact
            us before disputing anything related to Quill directly — we resolve nearly every
            billing issue within 1–2 business days.
          </p>
        </Section>

        <Section n={8} title="How to request a refund">
          <p>
            Email{" "}
            <a
              href="mailto:billing@quillpayed.com"
              className="text-white underline underline-offset-4 decoration-zinc-700 hover:decoration-white"
            >
              billing@quillpayed.com
            </a>{" "}
            from the email associated with your Quill account with the subject line{" "}
            <span className="font-mono text-white">&quot;Refund request — &lt;your email&gt;&quot;</span>{" "}
            and include the on-chain transaction hash of the payment you&apos;d like refunded.
            Approved refunds are sent back to the originating wallet within 3–5 business days,
            net of network gas.
          </p>
        </Section>

        <Section n={9} title="Statutory rights">
          <p>
            Nothing in this policy limits any non-waivable rights you may have under the consumer
            protection laws of your jurisdiction (including, where applicable, EU/UK withdrawal
            rights).
          </p>
        </Section>

        <Section n={10} title="Changes">
          <p>
            We may update this Policy. The version in effect at the time of your purchase governs
            that purchase. The &quot;Last updated&quot; date above shows the latest revision.
          </p>
        </Section>

        <div className="mt-16 pt-8 border-t border-zinc-900 flex items-center justify-between text-xs text-zinc-500">
          <div className="flex items-center gap-5">
            <Link to="/terms" data-testid="refunds-to-terms-link" className="hover:text-white transition-colors">Terms</Link>
            <Link to="/privacy" data-testid="refunds-to-privacy-link" className="hover:text-white transition-colors">Privacy</Link>
          </div>
          <div className="font-mono">© 2026 Quill</div>
        </div>
      </div>
    </div>
  );
};

export default Refunds;
